import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { openDispute } from "@/lib/disputes.functions";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertOctagon } from "lucide-react";
import { useTranslation } from "@/i18n";

const REASON_LABELS: Record<string, string> = {
  item_not_received: "Item not received",
  item_not_as_described: "Item not as described",
  damaged_item: "Damaged item",
  wrong_item: "Wrong item sent",
  suspected_counterfeit: "Suspected counterfeit",
  missing_contents: "Missing contents",
  tracking_problem: "Tracking problem",
  other: "Other",
};

const RESOLUTION_LABELS: Record<string, string> = {
  full_refund: "Full refund",
  partial_refund: "Partial refund",
  replacement: "Replacement",
  other: "Other",
};

export function DisputeReportDialog({
  orderId, orderTitle, open, onOpenChange,
}: {
  orderId: string | null;
  orderTitle?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation("commerce");
  const qc = useQueryClient();
  const navigate = useNavigate();
  const openDisputeFn = useServerFn(openDispute);
  const [reason, setReason] = useState<string>("");
  const [resolution, setResolution] = useState<string>("");
  const [description, setDescription] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  const reset = () => {
    setReason(""); setResolution(""); setDescription(""); setConfirmed(false);
  };

  const submit = useMutation({
    mutationFn: async () => {
      if (!orderId) throw new Error("Missing order");
      return openDisputeFn({
        data: {
          orderId,
          reason: reason as never,
          description,
          requestedResolution: resolution as never,
        },
      });
    },
    onSuccess: (res) => {
      toast.success(t("toast.disputeOpened", "Dispute opened — an admin will review"));
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["buyer-auto-payout"] });
      qc.invalidateQueries({ queryKey: ["disputes"] });
      onOpenChange(false); reset();
      navigate({ to: "/disputes/$disputeId", params: { disputeId: res.disputeId } });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const canSubmit = !!reason && !!resolution && description.trim().length >= 10 && confirmed && !submit.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); } onOpenChange(o); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertOctagon className="h-5 w-5 text-red-400" /> {t("disputeDialog.title", "Report a problem")}
          </DialogTitle>
          <DialogDescription>
            {t("disputeDialog.description", "Opening a dispute pauses seller payout until an admin reviews the case.")}
            {orderTitle ? <> {t("disputeDialog.orderLabel", "Order: {title}", { title: orderTitle })}</> : null}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>{t("disputeDialog.reasonLabel", "Reason")}</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger><SelectValue placeholder={t("disputeDialog.reasonPlaceholder", "Choose a reason")} /></SelectTrigger>
              <SelectContent>
                {Object.entries(REASON_LABELS).map(([v, l]) => (
                  <SelectItem key={v} value={v}>{t(`disputeReason.${v}`, l)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t("disputeDialog.resolutionLabel", "What outcome are you asking for?")}</Label>
            <Select value={resolution} onValueChange={setResolution}>
              <SelectTrigger><SelectValue placeholder={t("disputeDialog.resolutionPlaceholder", "Choose a resolution")} /></SelectTrigger>
              <SelectContent>
                {Object.entries(RESOLUTION_LABELS).map(([v, l]) => (
                  <SelectItem key={v} value={v}>{t(`disputeResolution.${v}`, l)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="dispute-desc">{t("disputeDialog.descriptionLabel", "Describe the issue")}</Label>
            <Textarea
              id="dispute-desc"
              placeholder={t("disputeDialog.descriptionPlaceholder", "Explain what happened (min 10 characters). You can attach photos on the dispute page after submitting.")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              maxLength={4000}
            />
            <p className="mt-1 text-xs text-muted-foreground">{t("disputeDialog.charCount", "{count}/4000 characters", { count: description.trim().length })}</p>
          </div>
          <label className="flex items-start gap-2 text-sm text-muted-foreground">
            <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(!!v)} className="mt-0.5" />
            <span>{t("disputeDialog.confirmCheckbox", "I understand this will open a formal dispute and pause seller payout.")}</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("disputeDialog.cancel", "Cancel")}</Button>
          <Button
            className="bg-red-600 hover:bg-red-500 text-white"
            disabled={!canSubmit}
            onClick={() => submit.mutate()}
          >
            {t("disputeDialog.submit", "Open dispute")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
